import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { useTripContext } from './TripDetailPage';
import { useAiDataVersion, useAiPanel } from '../../components/ai/AiPanelContext';
import { mealsApi } from '../../api/meals.api';
import { venuesApi } from '../../api/venues.api';
import type { PlaceBasicDetails } from '../../api/places.api';
import { categoryForSlot } from '../../components/trips/meals/mealSlots';
import { dayKey, dayToOpen } from '../../components/trips/meals/openDay';
import { isAlreadyGone } from '../../api/client';
import MealDayCard from '../../components/trips/meals/MealDayCard';
import GroceryPanel from '../../components/trips/meals/GroceryPanel';
import PantryPanel from '../../components/trips/meals/PantryPanel';
import { errorCode, usePantry } from '../../components/trips/meals/usePantry';
import { useMealStops } from '../../components/trips/meals/useMealStops';
import LinkedDeleteModal from '../../components/trips/LinkedDeleteModal';
import type { MealDraft } from '../../components/trips/meals/MealDraftForm';
import type { IngredientActions } from '../../components/trips/meals/MealIngredients';
import LoadingScreen from '../../components/ui/LoadingScreen';
import { toast } from '../../components/ui/Toast';
import { Utensils, Icon } from '../../components/ui/Icon';
import type { GroceryItem, InputUnit, Meal, MealSlot, Venue } from '../../types';
import { tripPermissions } from '../../utils/tripPermissions';

const COLLAPSED_KEY = 'meals-grocery-collapsed';

/** A meal at a place is titled after it and spends nothing (ADR-0014). */
const placeDraft = (title: string): MealDraft => ({ title, notes: null, ingredients: [], stop: { kind: 'none' } });

/**
 * The meal plan: days down the page, shopping list docked beside it.
 *
 * ADR-0008 — a meal is its own entity, not an itinerary stop. ADR-0024 — but it
 * may be linked to one, and then title and place are kept equal on both sides
 * by the server; this tab only reloads the days after a change that crosses.
 * The shopping list belongs to the trip, not to any one day.
 */
export default function MealsTab() {
  const { trip, days, loadDays } = useTripContext();
  // No specialist here, but gIA is on every tab: it answers with no hint.
  useAiPanel({ context: 'general' });
  const { t } = useTranslation();

  const [meals, setMeals] = useState<Meal[]>([]);
  const [grocery, setGrocery] = useState<GroceryItem[]>([]);
  // Fills the "which place is this at?" picker. A DB read, never a Places call
  // — ADR-0001. Grows when a searched place is saved from here (ADR-0013).
  const [venues, setVenues] = useState<Venue[]>([]);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(COLLAPSED_KEY) === '1');
  const today = format(new Date(), 'yyyy-MM-dd');
  // Not persisted on purpose: every visit reopens on the day that matters now.
  const [openDays, setOpenDays] = useState<Set<string>>(() => {
    const id = dayToOpen(days, today);
    return new Set(id ? [id] : []);
  });

  // The linked meal waiting for "and its stop too?" (ADR-0024).
  const [confirmDelete, setConfirmDelete] = useState<Meal | null>(null);

  const pantry = usePantry(trip.id, today, setMeals, setGrocery);
  const stops = useMealStops(trip.id, days, setMeals, loadDays);
  const refreshPantry = pantry.refresh;
  // ADR-0024: a linked stop's notes are its meal's ingredients, rewritten by the
  // server on every change, so the itinerary is re-read after one.
  const afterIngredients = (mealId: string) => {
    if (meals.some(m => m.id === mealId && m.activity)) void loadDays();
  };
  const ingredientActions: IngredientActions = {
    products: pantry.pantry,
    onAdd: async (mealId, ...rest) => {
      const ok = await pantry.addIngredient(mealId, ...rest);
      if (ok) afterIngredients(mealId);
      return ok;
    },
    onEdit: async (mealId, ...rest) => {
      const ok = await pantry.editIngredient(mealId, ...rest);
      if (ok) afterIngredients(mealId);
      return ok;
    },
    onRemove: async (mealId, ingredientId) => {
      await pantry.removeIngredient(mealId, ingredientId);
      afterIngredients(mealId);
    },
  };

  const { canEdit } = tripPermissions(trip);

  // ADR-0028: re-read when an accepted card changed a meal or the shopping list.
  const aiMealsVersion = useAiDataVersion('meals');
  useEffect(() => {
    let cancelled = false;
    mealsApi.list(trip.id)
      .then(plan => {
        if (cancelled) return;
        setMeals(plan.meals);
        setGrocery(plan.grocery);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [trip.id, aiMealsVersion]);

  useEffect(() => {
    let cancelled = false;
    venuesApi.list(trip.id)
      .then(list => { if (!cancelled) setVenues(list); })
      .catch(() => { /* the picker degrades to "no place", the plan still works */ });
    return () => { cancelled = true; };
  }, [trip.id]);

  const mealsByDay = useMemo(() => {
    const map = new Map<string, Meal[]>();
    for (const meal of meals) {
      const list = map.get(meal.dayId) ?? [];
      list.push(meal);
      map.set(meal.dayId, list);
    }
    return map;
  }, [meals]);

  function toggleDay(dayId: string) {
    setOpenDays(prev => {
      const next = new Set(prev);
      if (next.has(dayId)) next.delete(dayId); else next.add(dayId);
      return next;
    });
  }

  function toggleCollapsed() {
    setCollapsed(c => {
      const next = !c;
      localStorage.setItem(COLLAPSED_KEY, next ? '1' : '0');
      return next;
    });
  }

  // ── Meals ───────────────────────────────────────────────────────────────────

  const addMeal = useCallback(async (dayId: string, slot: MealSlot, draft: MealDraft, venueId?: string): Promise<Meal | null> => {
    try {
      const meal = await mealsApi.create(trip.id, {
        dayId, slot, title: draft.title,
        ...(draft.notes ? { notes: draft.notes } : {}),
        ...(draft.ingredients.length > 0 ? { ingredients: draft.ingredients } : {}),
        ...(venueId ? { venueId } : {}),
      });
      setMeals(prev => [...prev, meal]);
      if (meal.ingredients.length > 0) refreshPantry();
      return meal;
    } catch (e) {
      toast(t(errorCode(e) === 'UNIT_MISMATCH' ? 'meals.ingredients.unitMismatch' : 'meals.toast.saveFailed'), 'error');
      return null;
    }
  }, [trip.id, t, refreshPantry]);

  /**
   * Optimistic, then reconciled with what came back: changing `venueId` also
   * changes the `venue` relation the row renders, and only the server knows it.
   */
  async function patchMeal(mealId: string, patch: Partial<Omit<Meal, 'id' | 'dayId' | 'venue'>>): Promise<boolean> {
    const before = meals;
    setMeals(prev => prev.map(m => (m.id === mealId ? { ...m, ...patch } : m)));
    try {
      const saved = await mealsApi.update(trip.id, mealId, patch);
      setMeals(prev => prev.map(m => (m.id === mealId ? saved : m)));
      // The server synced the title or place onto the linked stop (ADR-0024).
      if (saved.activity && (patch.title !== undefined || patch.venueId !== undefined)) void loadDays();
      return true;
    } catch {
      setMeals(before);
      toast(t('meals.toast.saveFailed'), 'error');
      return false;
    }
  }

  /**
   * ADR-0013: a searched place becomes a Venue in Places. Reused by placeId so
   * the same restaurant for two dinners is one venue. Null when saving failed.
   */
  async function venueForPlace(place: PlaceBasicDetails, slot: MealSlot): Promise<Venue | null> {
    let venue = venues.find(v => v.placeId === place.placeId);
    if (!venue) {
      try {
        venue = await venuesApi.create(trip.id, {
          name: place.name,
          category: categoryForSlot(slot),
          placeId: place.placeId,
          // ADR-0001: resend what the search already resolved, or the server
          // pays a Place Details to recover it.
          address: place.address || null,
          latitude: place.lat,
          longitude: place.lng,
        });
      } catch {
        toast(t('meals.toast.saveFailed'), 'error');
        return null;
      }
      const created = venue;
      setVenues(prev => [...prev, created]);
    }
    return venue;
  }

  async function linkSearchedPlace(meal: Meal, place: PlaceBasicDetails) {
    const venue = await venueForPlace(place, meal.slot);
    if (venue) await patchMeal(meal.id, { venueId: venue.id });
  }

  async function addMealAtVenue(dayId: string, slot: MealSlot, venueId: string) {
    const venue = venues.find(v => v.id === venueId);
    return venue ? addMeal(dayId, slot, placeDraft(venue.name), venue.id) : null;
  }

  async function addMealAtPlace(dayId: string, slot: MealSlot, place: PlaceBasicDetails) {
    const venue = await venueForPlace(place, slot);
    return venue ? addMeal(dayId, slot, placeDraft(venue.name), venue.id) : null;
  }

  function deleteMeal(mealId: string) {
    const target = meals.find(m => m.id === mealId);
    if (target?.activity) setConfirmDelete(target);
    else void removeMeal(mealId, false);
  }

  async function removeMeal(mealId: string, withActivity: boolean) {
    setConfirmDelete(null);
    const before = meals;
    setMeals(prev => prev.filter(m => m.id !== mealId));
    // The grocery items keep their place with the label gone — the FK is
    // SetNull server-side, so mirror that here instead of refetching.
    setGrocery(prev => prev.map(i => (i.mealId === mealId ? { ...i, mealId: null } : i)));
    try {
      await mealsApi.delete(trip.id, mealId, withActivity);
      if (withActivity) void loadDays();
    } catch (e) {
      if (!isAlreadyGone(e)) {
        setMeals(before);
        toast(t('meals.toast.deleteFailed'), 'error');
      }
    }
    // Its ingredients went with it: that food is back in the pantry.
    pantry.refresh();
  }

  // ── Shopping list ───────────────────────────────────────────────────────────

  async function addGroceryItem(text: string, amount: number | null, unit: InputUnit): Promise<boolean> {
    try {
      const item = await mealsApi.createGroceryItem(trip.id, { text, unit, ...(amount ? { amount } : {}) });
      setGrocery(prev => [...prev, item]);
      pantry.refresh();
      return true;
    } catch (e) {
      toast(t(errorCode(e) === 'UNIT_MISMATCH' ? 'meals.ingredients.unitMismatch' : 'meals.toast.groceryFailed'), 'error');
      return false;
    }
  }

  async function toggleGroceryItem(item: GroceryItem, bought: boolean) {
    const before = grocery;
    setGrocery(prev => prev.map(i => (i.id === item.id ? { ...i, bought } : i)));
    try {
      await mealsApi.updateGroceryItem(trip.id, item.id, { bought });
    } catch {
      setGrocery(before);
      toast(t('meals.toast.groceryFailed'), 'error');
    }
    if (item.pantryItemId) pantry.refresh();
  }

  async function editGroceryAmount(item: GroceryItem, amount: number, unit: InputUnit): Promise<boolean> {
    try {
      const saved = await mealsApi.updateGroceryItem(trip.id, item.id, { amount, unit });
      setGrocery(prev => prev.map(i => (i.id === saved.id ? saved : i)));
      pantry.refresh();
      return true;
    } catch (e) {
      toast(t(errorCode(e) === 'UNIT_MISMATCH' ? 'meals.ingredients.unitMismatch' : 'meals.toast.groceryFailed'), 'error');
      return false;
    }
  }

  async function deleteGroceryItem(itemId: string) {
    const before = grocery;
    setGrocery(prev => prev.filter(i => i.id !== itemId));
    try {
      await mealsApi.deleteGroceryItem(trip.id, itemId);
    } catch (e) {
      if (!isAlreadyGone(e)) {
        setGrocery(before);
        toast(t('meals.toast.groceryFailed'), 'error');
      }
    }
    if (before.some(i => i.id === itemId && i.pantryItemId)) pantry.refresh();
  }

  async function clearBought() {
    const before = grocery;
    setGrocery(prev => prev.filter(i => !i.bought));
    try {
      const { deleted } = await mealsApi.clearBought(trip.id);
      toast(t('meals.grocery.cleared', { count: deleted }), 'success');
    } catch {
      setGrocery(before);
      toast(t('meals.toast.groceryFailed'), 'error');
    }
  }

  if (loading) return <LoadingScreen />;

  const groceryPanel = (
    <GroceryPanel
      items={grocery}
      meals={meals}
      readOnly={!canEdit}
      collapsed={collapsed}
      onToggleCollapsed={toggleCollapsed}
      products={pantry.pantry}
      onAdd={addGroceryItem}
      onToggle={toggleGroceryItem}
      onEditAmount={editGroceryAmount}
      onDelete={deleteGroceryItem}
      onClearBought={clearBought}
    />
  );

  return (
    <div className="flex flex-col h-full overflow-hidden animate-fade-in">
      {/* The tab frame every other trip tab uses: fixed header, scrolling body. */}
      <div className="shrink-0 px-4 md:px-8 pt-5 pb-4 bg-white dark:bg-slate-900 border-b border-slate-100 dark:border-slate-800">
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            <p className="font-mono text-[10px] tracking-[0.14em] uppercase text-slate-400 mb-0.5">
              {trip.destination.toUpperCase()}
            </p>
            <h2 className="font-serif italic font-semibold text-2xl md:text-3xl text-slate-900 dark:text-slate-100">
              {t('meals.title')}
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{t('meals.subtitle')}</p>
          </div>
          <div className="hidden sm:flex shrink-0 w-11 h-11 rounded-2xl bg-teal-50 dark:bg-teal-900/20 items-center justify-center">
            <Icon as={Utensils} size={20} className="text-teal-600 dark:text-teal-400" />
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto px-4 md:px-8 py-5 md:py-6">
        <div className="flex flex-col lg:flex-row gap-4 lg:gap-5 items-start">
          {/* No empty state: every day card already renders its four slots with
              an add button in each, so a banner saying "nothing planned yet"
              only pushes the one actionable thing down the page. */}
          <div className="flex-1 min-w-0 space-y-2.5">
            {days.map(day => (
              <MealDayCard
                key={day.id}
                day={day}
                meals={mealsByDay.get(day.id) ?? []}
                venues={venues}
                onPickPlace={linkSearchedPlace}
                ingredients={ingredientActions}
                stops={stops}
                readOnly={!canEdit}
                open={openDays.has(day.id)}
                isToday={dayKey(day) === today}
                onToggle={() => toggleDay(day.id)}
                onAdd={(slot, draft) => addMeal(day.id, slot, draft)}
                onAddAtVenue={(slot, venueId) => addMealAtVenue(day.id, slot, venueId)}
                onAddAtPlace={(slot, place) => addMealAtPlace(day.id, slot, place)}
                onRename={(mealId, title) => patchMeal(mealId, { title })}
                onUpdate={patchMeal}
                onDelete={deleteMeal}
              />
            ))}
          </div>

          {collapsed ? groceryPanel : (
            <div className="shrink-0 w-full lg:w-72 space-y-3 self-start lg:sticky lg:top-0">
              {groceryPanel}
              <PantryPanel rows={pantry.pantry} readOnly={!canEdit} onAddMissing={pantry.addMissing} />
            </div>
          )}
        </div>
      </div>

      <LinkedDeleteModal
        open={confirmDelete !== null}
        title={t('meals.deleteLinked.title')}
        body={t('meals.deleteLinked.body')}
        onlyLabel={t('meals.deleteLinked.only')}
        onOnly={() => { if (confirmDelete) void removeMeal(confirmDelete.id, false); }}
        onBoth={() => { if (confirmDelete) void removeMeal(confirmDelete.id, true); }}
        onCancel={() => setConfirmDelete(null)}
      />
    </div>
  );
}
